// ============================================================================
// CANCUN100 — comprobantes.js  (APP 2/07 · S4: comprobantes con liga firmada)
// ----------------------------------------------------------------------------
// El bucket `comprobantes` deja de ser público. Para MOSTRAR un comprobante se firma su ruta
// (createSignedUrl, 1 h, sin `download`); para GUARDAR se sigue escribiendo la URL de siempre
// (compatibilidad) y además la ruta en `comprobante_path` cuando la tabla ya tiene la columna.
//  · rutaComprobante(x): acepta una ruta o la URL pública vieja y devuelve la ruta dentro del bucket.
//  · urlFirmadaComprobante(x): liga firmada (con caché en memoria mientras dura la pestaña).
//  · <ComprobanteFirmado url path>{(href) => …}</ComprobanteFirmado>: resuelve la liga y pinta lo que le pases.
//  · subirComprobante(file, filename): sube y devuelve { path, url }.
// ============================================================================
import React, { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';

export const BUCKET_COMPROBANTES = 'comprobantes';
const SEGUNDOS = 3600;

// Tablas que YA tienen la columna comprobante_path (se prende cuando Claude aplique la SQL propuesta en APP 2/07).
// Mientras esté apagada, la ruta se saca de la URL guardada: funciona igual aunque el bucket sea privado.
export const TABLAS_CON_PATH = new Set([]);
export const camposRuta = (tabla, path) => (TABLAS_CON_PATH.has(tabla) && path ? { comprobante_path: path } : {});

// Ruta dentro del bucket a partir de una ruta o de la URL pública/firmada vieja.
export const rutaComprobante = (x) => {
  const s = String(x || '').trim();
  if (!s) return null;
  if (!/^https?:\/\//i.test(s)) return s.replace(/^\/+/, '');
  const m = s.match(/\/comprobantes\/([^?#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
};

const cache = new Map(); // ruta → { url, vence }
export const urlFirmadaComprobante = async (urlOPath, segundos = SEGUNDOS) => {
  const ruta = rutaComprobante(urlOPath);
  if (!ruta) return null;
  const hit = cache.get(ruta);
  if (hit && hit.vence > Date.now()) return hit.url;
  const { data, error } = await supabase.storage.from(BUCKET_COMPROBANTES).createSignedUrl(ruta, segundos); // sin download: se abre en pestaña
  if (error || !data?.signedUrl) return null;
  cache.set(ruta, { url: data.signedUrl, vence: Date.now() + (segundos - 60) * 1000 });
  return data.signedUrl;
};

// Sube el archivo y devuelve la ruta y la URL "de siempre" (publicUrl: deja de abrir cuando el bucket sea privado, pero sirve para sacar la ruta).
export const subirComprobante = async (file, filename, opciones = { cacheControl: '3600', upsert: false }) => {
  const { error } = await supabase.storage.from(BUCKET_COMPROBANTES).upload(filename, file, opciones);
  if (error) throw error;
  return { path: filename, url: supabase.storage.from(BUCKET_COMPROBANTES).getPublicUrl(filename).data.publicUrl };
};

// Hook: liga firmada de un comprobante (null mientras carga o si no hay).
export const useComprobanteFirmado = (url, path) => {
  const ref = path || url || null;
  const [href, setHref] = useState(null);
  useEffect(() => {
    let vivo = true;
    setHref(null);
    if (!ref) return undefined;
    urlFirmadaComprobante(ref).then((u) => { if (vivo) setHref(u); }, () => { if (vivo) setHref(null); });
    return () => { vivo = false; };
  }, [ref]);
  return href;
};

// Render prop: {(href) => <a href={href}>…</a>}. Si no hay comprobante, no pinta nada; mientras firma, pinta con href '#'.
export function ComprobanteFirmado({ url, path, children }) {
  const href = useComprobanteFirmado(url, path);
  if (!(path || url)) return null;
  return children(href || '#', !href);
}
